import React from 'react';
import { Box, Unstable_Grid2 as Grid } from '@mui/material';
import { Form, FormRenderProps } from 'react-final-form';
import LoadingButton from '@mui/lab/LoadingButton';

import { Button, Card, CardContent, Typography } from '@occc/ui-design-system';
import { Select, TextField, TextFieldProps } from '@occc-app/design-system';
import { parseValueToOnlyDigits } from '@occc/utils';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';

import {
  ScanDataSubmissionGroupsCreateFormValues,
  ScanDataSubmissionGroupsCreateFormValuesPaths,
} from './scan-data-submission-groups-create.types';
import {
  initialValues,
  submissionTypeOptions,
  tobaccoCompanyOptions,
} from './scan-data-submission-groups-create.constants';
import { SubmissionGroupLocationsTable } from './components/submission-group-locations-table';
import { validateScanDataSubmissionGroupCreate } from './scan-data-submission-groups-create.validation';
import { TestAndCreateSFTPCredentialsStatusModal } from './components/test-and-create-status.modal';

type ScanDataSubmissionGroupCreateProps = ReturnType<typeof useBulkSelect> & {
  onSubmit: (value: ScanDataSubmissionGroupsCreateFormValues) => void;
  onCancel: () => void;
  isEntityCreated: boolean;
  isErrorHappened: boolean;
  showCreationStatusModal: boolean;
  onCreationStatusModalHide: () => void;
  onCreationStatusModalOk: () => void;
};

const onlyDigitsFieldFieldProps: TextFieldProps['fieldProps'] = {
  parse: parseValueToOnlyDigits,
};

export const ScanDataSubmissionGroupsCreate = ({
  onSubmit,
  onCancel,
  isEntityCreated,
  isErrorHappened,
  showCreationStatusModal,
  onCreationStatusModalHide,
  onCreationStatusModalOk,
  bulkSelectedItems,
  handleBulkSelectMultiple,
  handleBulkDeselectAll,
  handleItemSelect,
}: ScanDataSubmissionGroupCreateProps) => {
  const renderForm = React.useCallback(
    ({
      handleSubmit,
      submitting,
      values,
    }: FormRenderProps<ScanDataSubmissionGroupsCreateFormValues>) => {
      return (
        <>
          <form onSubmit={handleSubmit}>
            <Card sx={{ mb: 1 }}>
              <CardContent>
                <Typography variant='h6' mb={2}>
                  Group Details
                </Typography>

                <Grid container spacing={3}>
                  <Grid xs={12} md={3}>
                    <TextField name='name' label='Submission Group Name' margin='none' />
                  </Grid>

                  <Grid xs={12} md={3}>
                    <Select
                      name={
                        'tobaccoCompany' satisfies ScanDataSubmissionGroupsCreateFormValuesPaths
                      }
                      label='Tobacco Company'
                      data={tobaccoCompanyOptions}
                      formControlProps={{ size: 'medium' }}
                    />
                  </Grid>

                  <Grid xs={12} md={3}>
                    <Select
                      name={
                        'submissionType' satisfies ScanDataSubmissionGroupsCreateFormValuesPaths
                      }
                      label='Submission Type'
                      data={submissionTypeOptions}
                      formControlProps={{ size: 'medium' }}
                    />
                  </Grid>

                  <Grid xs={12} md={3}>
                    <TextField
                      name={
                        'sftpDetails.submitterID' satisfies ScanDataSubmissionGroupsCreateFormValuesPaths
                      }
                      label='Submitter ID'
                      margin='none'
                      fieldProps={onlyDigitsFieldFieldProps}
                    />
                  </Grid>
                </Grid>

                <Typography variant='h6' mb={2} mt={4}>
                  SFTP Details
                </Typography>

                <Grid container spacing={3}>
                  <Grid xs={12} md={3}>
                    <TextField
                      name={
                        'sftpDetails.username' satisfies ScanDataSubmissionGroupsCreateFormValuesPaths
                      }
                      label='Username'
                      margin='none'
                    />
                  </Grid>

                  <Grid xs={12} md={3}>
                    <TextField
                      name={
                        'sftpDetails.password' satisfies ScanDataSubmissionGroupsCreateFormValuesPaths
                      }
                      label='Password'
                      margin='none'
                      type='password'
                    />
                  </Grid>

                  <Grid xs={12} md={3}>
                    <TextField
                      name={
                        'sftpDetails.portNumber' satisfies ScanDataSubmissionGroupsCreateFormValuesPaths
                      }
                      label='Port Number'
                      margin='none'
                      fieldProps={onlyDigitsFieldFieldProps}
                    />
                  </Grid>

                  <Grid xs={12} md={3}>
                    <TextField
                      name={
                        'sftpDetails.submissionURL' satisfies ScanDataSubmissionGroupsCreateFormValuesPaths
                      }
                      label='Submission URL'
                      margin='none'
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <TestAndCreateSFTPCredentialsStatusModal
              show={showCreationStatusModal}
              onHide={onCreationStatusModalHide}
              isEntityCreated={isEntityCreated}
              isErrorHappened={isErrorHappened}
              onOkClick={onCreationStatusModalOk}
            />
          </form>

          <SubmissionGroupLocationsTable
            cpg={values.tobaccoCompany}
            bulkSelectedItems={bulkSelectedItems}
            handleBulkSelectMultiple={handleBulkSelectMultiple}
            handleBulkDeselectAll={handleBulkDeselectAll}
            handleItemSelect={handleItemSelect}
          />

          <Box display='flex' justifyContent='flex-end' sx={{ mt: 1 }}>
            <Button role='button' onClick={onCancel} sx={{ mr: 3 }}>
              Cancel
            </Button>

            <LoadingButton
              loading={submitting}
              variant='contained'
              color='primary'
              onClick={handleSubmit}
              disabled={submitting}
            >
              Save
            </LoadingButton>
          </Box>
        </>
      );
    },
    [
      bulkSelectedItems,
      handleBulkDeselectAll,
      handleBulkSelectMultiple,
      handleItemSelect,
      isEntityCreated,
      isErrorHappened,
      onCancel,
      onCreationStatusModalHide,
      onCreationStatusModalOk,
      showCreationStatusModal,
    ],
  );

  return (
    <Form<ScanDataSubmissionGroupsCreateFormValues>
      onSubmit={onSubmit}
      render={renderForm}
      initialValues={initialValues}
      validate={validateScanDataSubmissionGroupCreate}
    />
  );
};
